import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Building2, Briefcase, DollarSign, User, CreditCard, TrendingUp,
  ShieldCheck, ArrowRight, ArrowLeft, CheckCircle2, Sparkles,
  Phone, Send, Mail, Loader2, PartyPopper,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { DANIEL_SCENARIO, SARAH_SCENARIO, MARCUS_SCENARIO } from "../data/demoScenarios";

const SCENARIOS = {
  daniel: DANIEL_SCENARIO,
  sarah: SARAH_SCENARIO,
  marcus: MARCUS_SCENARIO,
};
const API = process.env.REACT_APP_BACKEND_URL;
const STEP_ICONS = [Briefcase, CreditCard, DollarSign, User, TrendingUp, DollarSign, ShieldCheck];

export default function DemoRunnerPage() {
  const { scenarioId } = useParams();
  const navigate = useNavigate();
  const scenario = SCENARIOS[scenarioId] || DANIEL_SCENARIO;

  // Phases: intro → form-walk → loading → results → lender-connect
  const [phase, setPhase] = useState("intro");
  const [stepIdx, setStepIdx] = useState(0);
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedBank, setSelectedBank] = useState(null);

  const runQualification = async () => {
    setLoading(true);
    setPhase("loading");
    try {
      const res = await axios.post(`${API}/api/qualify`, scenario.form_data);
      setResults(res.data);
      setTimeout(() => {
        setPhase("results");
        setLoading(false);
      }, 2500);
    } catch (e) {
      toast.error("Demo failed to load results");
      setLoading(false);
      setPhase("form-walk");
    }
  };

  const nextStep = () => {
    if (stepIdx < scenario.form_steps.length - 1) {
      setStepIdx(stepIdx + 1);
    } else {
      runQualification();
    }
  };

  const prevStep = () => {
    if (stepIdx > 0) setStepIdx(stepIdx - 1);
    else setPhase("intro");
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#f8fffe] via-white to-[#f0fafa] pt-20 pb-16 px-4 lg:px-8">
      <div className="max-w-4xl mx-auto">
        {/* Demo mode banner */}
        <div className="mb-6 flex items-center justify-between gap-2 rounded-full bg-[#52BFB8]/10 border border-[#52BFB8]/30 px-4 py-2 text-sm">
          <span className="flex items-center gap-2 text-[#3E9690] font-semibold">
            <Sparkles className="w-4 h-4" /> Live Demo · Scenario: {scenario.meet.name}
          </span>
          <button
            onClick={() => navigate("/admin")}
            className="text-xs text-gray-500 hover:text-gray-800"
            data-testid="demo-exit"
          >
            Exit demo →
          </button>
        </div>

        {/* ═══ Phase 1: MEET DANIEL ═══ */}
        {phase === "intro" && <IntroCard scenario={scenario} onStart={() => setPhase("form-walk")} />}

        {/* ═══ Phase 2: FORM WALK-THROUGH ═══ */}
        {phase === "form-walk" && (
          <FormWalkThrough
            scenario={scenario}
            stepIdx={stepIdx}
            total={scenario.form_steps.length}
            onNext={nextStep}
            onPrev={prevStep}
          />
        )}

        {/* ═══ Phase 3: LOADING ═══ */}
        {phase === "loading" && <LoadingScreen name={scenario.meet.name} />}

        {/* ═══ Phase 4: RESULTS ═══ */}
        {phase === "results" && results && (
          <ResultsScreen
            scenario={scenario}
            results={results}
            onConnect={(bank) => {
              setSelectedBank(bank);
              setPhase("lender-connect");
            }}
          />
        )}

        {/* ═══ Phase 5: LENDER CONNECT ═══ */}
        {phase === "lender-connect" && (
          <LenderConnectScreen
            scenario={scenario}
            bank={selectedBank}
            onDone={() => {
              toast.success("Demo complete!");
              navigate("/admin");
            }}
            onBack={() => setPhase("results")}
          />
        )}
      </div>
    </div>
  );
}

/* ─────────── PHASE 1: Meet Card ─────────── */
function IntroCard({ scenario, onStart }) {
  const m = scenario.meet;
  return (
    <div className="bg-white rounded-3xl shadow-xl border border-gray-100 overflow-hidden" data-testid="demo-intro-card">
      <div className="bg-gradient-to-br from-[#52BFB8] to-[#3E9690] p-8 sm:p-10 text-white">
        <div className="flex flex-col sm:flex-row items-start gap-6">
          <div className="w-24 h-24 rounded-full bg-white text-[#3E9690] font-bold text-3xl flex items-center justify-center flex-shrink-0 shadow-lg">
            {m.photo_initials}
          </div>
          <div className="flex-1">
            <p className="text-xs tracking-[0.25em] uppercase text-white/80 font-semibold mb-2">Meet</p>
            <h1 className="text-4xl sm:text-5xl font-bold mb-2 font-cabinet">{m.name}</h1>
            <p className="text-lg text-white/90">{m.age} yrs · {m.role}</p>
            <p className="text-sm text-white/80 mt-2 max-w-2xl">{m.bio}</p>
          </div>
        </div>
      </div>

      <div className="p-8 sm:p-10 space-y-6">
        <div className="grid sm:grid-cols-2 gap-4">
          <Stat icon={Building2} label="Employer" value={m.employer} />
          <Stat icon={DollarSign} label="Monthly Salary" value={`$${m.monthly_salary.toLocaleString()}`} />
          <Stat icon={CreditCard} label="Existing Debt" value={`$${m.existing_debts}/mo`} />
          <Stat icon={TrendingUp} label={m.loan_type_label} value={`$${m.loan_ask.toLocaleString()}`} highlight />
        </div>

        <div className="bg-gray-50 rounded-2xl p-5">
          <p className="text-xs uppercase tracking-wider text-gray-500 mb-3 font-semibold">Debt breakdown</p>
          <div className="space-y-2">
            {m.debt_breakdown.map((d) => (
              <div key={d.label} className="flex justify-between text-sm">
                <span className="text-gray-600">{d.label}</span>
                <span className="font-mono font-semibold text-[#4A4A4A]">${d.amount}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="rounded-2xl bg-[#52BFB8]/8 border border-[#52BFB8]/30 p-5 flex items-start gap-3">
          <Sparkles className="w-5 h-5 text-[#52BFB8] mt-0.5 flex-shrink-0" />
          <p className="text-sm text-[#3E9690]">
            <strong>His goal:</strong> {m.goal}
          </p>
        </div>

        <Button onClick={onStart} className="btn-primary w-full h-14 text-base" data-testid="demo-start-btn">
          Start {scenario.meet.name.split(" ")[0]}&apos;s Application
          <ArrowRight className="w-5 h-5 ml-2" />
        </Button>
      </div>
    </div>
  );
}

function Stat({ icon: Icon, label, value, highlight }) {
  return (
    <div className={`rounded-2xl p-4 border ${highlight ? "bg-[#52BFB8]/10 border-[#52BFB8]/30" : "bg-white border-gray-200"}`}>
      <div className="flex items-center gap-2 mb-1">
        <Icon className={`w-4 h-4 ${highlight ? "text-[#52BFB8]" : "text-gray-400"}`} />
        <p className="text-xs text-gray-500 uppercase tracking-wider">{label}</p>
      </div>
      <p className={`text-xl font-bold ${highlight ? "text-[#52BFB8]" : "text-[#2D3748]"}`}>{value}</p>
    </div>
  );
}

/* ─────────── PHASE 2: Form Walk ─────────── */
function FormWalkThrough({ scenario, stepIdx, total, onNext, onPrev }) {
  const step = scenario.form_steps[stepIdx];
  const Icon = STEP_ICONS[stepIdx] || CheckCircle2;
  const progressPct = ((stepIdx + 1) / total) * 100;

  return (
    <div className="bg-white rounded-3xl shadow-xl border border-gray-100 overflow-hidden" data-testid={`demo-step-${stepIdx + 1}`}>
      {/* Progress */}
      <div className="p-6 pb-3">
        <div className="flex items-center justify-between mb-2 text-xs text-gray-500">
          <span>Step {stepIdx + 1} of {total}</span>
          <span>{Math.round(progressPct)}% complete</span>
        </div>
        <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
          <div className="h-full bg-gradient-to-r from-[#52BFB8] to-[#3E9690] transition-all duration-500" style={{ width: `${progressPct}%` }} />
        </div>
      </div>

      <div className="px-6 sm:px-10 py-8">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-12 h-12 rounded-xl bg-[#52BFB8]/10 flex items-center justify-center">
            <Icon className="w-6 h-6 text-[#52BFB8]" />
          </div>
          <div>
            <p className="text-xs uppercase tracking-wider text-gray-400">Question</p>
            <h2 className="text-2xl font-bold text-[#2D3748]">{step.title}</h2>
          </div>
        </div>

        <div className="bg-gradient-to-r from-[#52BFB8]/10 to-transparent border-l-4 border-[#52BFB8] rounded-r-xl p-5 mb-6">
          <p className="text-xs uppercase tracking-wider text-[#3E9690] mb-1 font-semibold">Daniel&apos;s answer</p>
          <p className="text-xl font-semibold text-[#2D3748]">{step.answer}</p>
        </div>

        <div className="space-y-3">
          {step.details.map((d) => (
            <div key={d.label} className="flex justify-between items-center py-2 border-b border-gray-100">
              <span className="text-sm text-gray-600">{d.label}</span>
              <span className="font-semibold text-[#2D3748]">{d.value}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="flex justify-between items-center px-6 sm:px-10 py-5 bg-gray-50 border-t border-gray-100">
        <Button variant="outline" onClick={onPrev} data-testid="demo-prev-btn">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
        <Button onClick={onNext} className="btn-primary" data-testid="demo-next-btn">
          {stepIdx === total - 1 ? (
            <>
              Get {scenario.meet.name.split(" ")[0]}&apos;s Results
              <Sparkles className="w-4 h-4 ml-2" />
            </>
          ) : (
            <>
              Next
              <ArrowRight className="w-4 h-4 ml-2" />
            </>
          )}
        </Button>
      </div>
    </div>
  );
}

/* ─────────── PHASE 3: Loading ─────────── */
function LoadingScreen({ name }) {
  return (
    <div className="bg-white rounded-3xl shadow-xl border border-gray-100 p-12 text-center" data-testid="demo-loading">
      <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-gradient-to-br from-[#52BFB8] to-[#3E9690] flex items-center justify-center shadow-lg">
        <Loader2 className="w-10 h-10 text-white animate-spin" />
      </div>
      <h2 className="text-2xl font-bold text-[#2D3748] mb-2">Running the AI qualification engine...</h2>
      <p className="text-gray-500">Matching {name} against 6 Bahamian lenders</p>
      <div className="mt-8 flex justify-center gap-2">
        {["TDSR calc", "Bank rules", "Rate match", "Term optimizer"].map((s, i) => (
          <span key={s} className="text-xs px-3 py-1 rounded-full bg-gray-100 text-gray-600 opacity-0 animate-[fadeIn_0.4s_forwards]" style={{ animationDelay: `${i * 400}ms` }}>
            ✓ {s}
          </span>
        ))}
      </div>
    </div>
  );
}

/* ─────────── PHASE 4: Results ─────────── */
function ResultsScreen({ scenario, results, onConnect }) {
  const matched = results.matched_banks || [];
  // Top-level tdsr_percentage is often 0; compute the best-representative TDSR from matched banks
  const bankTdsrs = matched.map((b) => b.tdsr_percentage).filter((v) => v > 0);
  const income = results.total_monthly_income || scenario.form_data.gross_monthly_salary;
  const debts = results.total_monthly_debt || 0;
  const displayTdsr =
    bankTdsrs.length > 0
      ? Math.min(...bankTdsrs)
      : income > 0
      ? (debts / income) * 100
      : 0;
  return (
    <div className="space-y-6" data-testid="demo-results">
      {/* Congrats banner */}
      <div className="bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-3xl p-8 text-white text-center shadow-xl">
        <PartyPopper className="w-12 h-12 mx-auto mb-3" />
        <h2 className="text-3xl sm:text-4xl font-bold mb-2 font-cabinet">Congratulations, {scenario.meet.name.split(" ")[0]}!</h2>
        <p className="text-white/90 text-lg">You pre-qualify with {matched.length} lender{matched.length !== 1 ? "s" : ""}</p>
      </div>

      {/* TDSR & summary */}
      <div className="bg-white rounded-3xl shadow-xl p-6 grid grid-cols-3 gap-4">
        <div className="text-center">
          <p className="text-xs uppercase tracking-wider text-gray-400 mb-1">TDSR</p>
          <p className="text-3xl font-bold text-[#52BFB8]" data-testid="demo-tdsr-value">{displayTdsr.toFixed(1)}%</p>
        </div>
        <div className="text-center border-x border-gray-100">
          <p className="text-xs uppercase tracking-wider text-gray-400 mb-1">Requested</p>
          <p className="text-3xl font-bold text-[#2D3748]">${results.requested_amount?.toLocaleString()}</p>
        </div>
        <div className="text-center">
          <p className="text-xs uppercase tracking-wider text-gray-400 mb-1">Best offer</p>
          <p className="text-3xl font-bold text-[#3E9690]">
            ${Math.max(...matched.map(b => b.max_approved_amount)).toLocaleString()}
          </p>
        </div>
      </div>

      {/* Bank cards */}
      <div className="space-y-3">
        {matched.map((bank) => (
          <div key={bank.bank_name} className="bg-white rounded-2xl shadow-md border border-gray-100 p-5 flex flex-col sm:flex-row items-start sm:items-center gap-4 hover:border-[#52BFB8]/40 transition-all">
            <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#52BFB8]/20 to-[#52BFB8]/5 border border-[#52BFB8]/30 flex items-center justify-center flex-shrink-0">
              <Building2 className="w-7 h-7 text-[#52BFB8]" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-[#2D3748]">{bank.bank_name}</p>
              <div className="flex gap-3 text-xs text-gray-500 mt-1">
                <span>Rate: <strong className="text-[#3E9690]">{bank.interest_rate}%</strong></span>
                <span>·</span>
                <span>Term: <strong>{bank.max_term_years} yrs</strong></span>
                <span>·</span>
                <span>Monthly: <strong>${bank.estimated_monthly_payment?.toLocaleString()}</strong></span>
              </div>
            </div>
            <div className="text-right sm:text-right">
              <p className="text-xl font-bold text-[#2D3748]">${bank.max_approved_amount?.toLocaleString()}</p>
              <Button size="sm" className="btn-primary mt-2" onClick={() => onConnect(bank)} data-testid={`connect-${bank.bank_name.replace(/\s/g, "-")}`}>
                <Send className="w-3 h-3 mr-1" />
                Connect with Loan Officer
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ─────────── PHASE 5: Lender Connect ─────────── */
function LenderConnectScreen({ scenario, bank, onDone, onBack }) {
  return (
    <div className="bg-white rounded-3xl shadow-xl border border-gray-100 p-8 sm:p-10" data-testid="demo-lender-connect">
      <div className="text-center mb-8">
        <div className="w-16 h-16 mx-auto rounded-full bg-emerald-100 flex items-center justify-center mb-4">
          <CheckCircle2 className="w-8 h-8 text-emerald-600" />
        </div>
        <h2 className="text-3xl font-bold text-[#2D3748] mb-2 font-cabinet">Application Sent!</h2>
        <p className="text-gray-500">A loan officer from <strong>{bank.bank_name}</strong> will reach out within 24 hours.</p>
      </div>

      <div className="bg-gray-50 rounded-2xl p-5 space-y-3 mb-6">
        <p className="text-xs uppercase tracking-wider text-gray-500 font-semibold">What happens next</p>
        {[
          { icon: Mail, text: `Confirmation email sent to ${scenario.meet.name.split(" ")[0].toLowerCase()}@moe.gov.bs` },
          { icon: Phone, text: "Loan officer contact within 24 hours" },
          { icon: ShieldCheck, text: "Final KYC verification & document collection" },
          { icon: CheckCircle2, text: "Funds disbursed once approved (typically 3-5 business days)" },
        ].map((s) => {
          const Icon = s.icon;
          return (
            <div key={s.text} className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-lg bg-[#52BFB8]/10 flex items-center justify-center flex-shrink-0">
                <Icon className="w-4 h-4 text-[#52BFB8]" />
              </div>
              <span className="text-sm text-[#4A4A4A] mt-1">{s.text}</span>
            </div>
          );
        })}
      </div>

      <div className="rounded-2xl bg-gradient-to-r from-[#52BFB8]/8 to-transparent border border-[#52BFB8]/20 p-4 mb-6">
        <p className="text-xs font-semibold text-[#3E9690] mb-2 uppercase tracking-wider">Application snapshot</p>
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div><span className="text-gray-500">Applicant:</span> <strong>{scenario.meet.name}</strong></div>
          <div><span className="text-gray-500">Lender:</span> <strong>{bank.bank_name}</strong></div>
          <div><span className="text-gray-500">Amount:</span> <strong>${bank.max_approved_amount?.toLocaleString()}</strong></div>
          <div><span className="text-gray-500">Rate:</span> <strong>{bank.interest_rate}% p.a.</strong></div>
          <div><span className="text-gray-500">Term:</span> <strong>{bank.max_term_years} years</strong></div>
          <div><span className="text-gray-500">Monthly:</span> <strong>${bank.estimated_monthly_payment?.toLocaleString()}</strong></div>
        </div>
      </div>

      <div className="flex gap-3">
        <Button variant="outline" onClick={onBack} className="flex-1" data-testid="demo-back-to-results">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Results
        </Button>
        <Button onClick={onDone} className="btn-primary flex-1" data-testid="demo-finish">
          Finish Demo
          <CheckCircle2 className="w-4 h-4 ml-2" />
        </Button>
      </div>
    </div>
  );
}
